import React, { useState, useEffect } from "react";
import { db, collection, getDocs, addDoc, updateDoc, deleteDoc, doc, setDoc, onSnapshot, serverTimestamp } from "../config/firebase";
import { fmt } from "../config/firebase";
import { uploadProductImage } from "../config/supabase";
import "./AdminComponents.css";

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [productSearch, setProductSearch] = useState("");
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [hiddenCategories, setHiddenCategories] = useState([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    price: "",
    originalPrice: "",
    description: "",
    image: "",
    stock: "",
    variants: [],
    isNewArrival: false,
    featured: false,
    featuredOrder: ""
  });
  const [variantInput, setVariantInput] = useState({ label: "", price: "", originalPrice: "" });

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, "settings", "hiddenCategories"),
      (snap) => {
        if (snap.exists()) {
          setHiddenCategories(snap.data().categories || []);
        } else {
          setHiddenCategories([]);
        }
      },
      (err) => {
        console.error("Error fetching hidden categories:", err);
      }
    );
    return () => unsubscribe();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const snap = await getDocs(collection(db, "products"));
      const items = [];
      snap.forEach((docSnap) => {
        items.push({ id: docSnap.id, ...docSnap.data() });
      });
      setProducts(items);
    } catch (err) {
      console.error("Error fetching products:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategoryVisibility = async (category) => {
    const isCurrentlyHidden = hiddenCategories.includes(category);
    const updated = isCurrentlyHidden
      ? hiddenCategories.filter((c) => c !== category)
      : [...hiddenCategories, category];
    try {
      await setDoc(doc(db, "settings", "hiddenCategories"), { categories: updated });
    } catch (err) {
      console.error("Error updating hidden categories:", err);
    }
  };

  const resetForm = (prefillCategory = "") => {
    setFormData({
      name: "",
      category: prefillCategory,
      price: "",
      originalPrice: "",
      description: "",
      image: "",
      stock: "",
      variants: [],
      isNewArrival: false,
      featured: false,
      featuredOrder: ""
    });
    setVariantInput({ label: "", price: "", originalPrice: "" });
    setEditingId(null);
  };

  const handleAddClick = () => {
    const prefillCategory = selectedCategory !== "All" ? selectedCategory : "";
    resetForm(prefillCategory);
    setShowForm((prev) => !prev);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleImageFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Sirf image files upload karo");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert("Image 5MB se choti honi chahiye");
      return;
    }
    if (!formData.category.trim()) {
      alert("Pehle category bharo, taaki image sahi folder mein save ho");
      return;
    }

    try {
      setImageUploading(true);
      const url = await uploadProductImage(file, formData.category);
      setFormData((prev) => ({ ...prev, image: url }));
    } catch (err) {
      console.error("Image upload failed:", err);
      alert("Image upload fail ho gaya, dobara try karo");
    } finally {
      setImageUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: Number(formData.price),
        originalPrice: formData.originalPrice ? Number(formData.originalPrice) : null,
        stock: Number(formData.stock),
        variants: formData.variants.map((v) => ({
          ...v,
          price: Number(v.price),
          originalPrice: v.originalPrice ? Number(v.originalPrice) : null
        })),
        isNewArrival: !!formData.isNewArrival,
        featured: !!formData.featured,
        featuredOrder: formData.featured
          ? (formData.featuredOrder !== "" ? Number(formData.featuredOrder) : 0)
          : null,
        updatedAt: serverTimestamp()
      };

      if (editingId) {
        await updateDoc(doc(db, "products", editingId), data);
        setProducts((prev) =>
          prev.map((p) => (p.id === editingId ? { ...p, ...data } : p))
        );
      } else {
        data.createdAt = serverTimestamp();
        const ref = await addDoc(collection(db, "products"), data);
        setProducts((prev) => [...prev, { id: ref.id, ...data }]);
      }

      resetForm();
      setShowForm(false);
    } catch (err) {
      console.error("Error saving product:", err);
    }
  };

  const handleEdit = (product) => {
    setFormData({
      name: product.name || "",
      category: product.category || "",
      price: product.price || "",
      originalPrice: product.originalPrice || "",
      description: product.description || "",
      image: product.image || "",
      stock: product.stock || "",
      variants: product.variants || [],
      isNewArrival: product.isNewArrival || false,
      featured: product.featured || false,
      featuredOrder: product.featuredOrder ?? ""
    });
    setEditingId(product.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await deleteDoc(doc(db, "products", id));
      setProducts((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error("Error deleting product:", err);
    }
  };

  const addVariant = () => {
    if (!variantInput.label || !variantInput.price) return;
    setFormData((prev) => ({
      ...prev,
      variants: [
        ...prev.variants,
        {
          label: variantInput.label,
          price: Number(variantInput.price),
          originalPrice: variantInput.originalPrice ? Number(variantInput.originalPrice) : null
        }
      ]
    }));
    setVariantInput({ label: "", price: "", originalPrice: "" });
  };

  const removeVariant = (idx) => {
    setFormData((prev) => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== idx)
    }));
  };

  const csvEscape = (value) => {
    const str = String(value ?? "");
    if (/[",\n]/.test(str)) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const encodeVariants = (variants) => {
    if (!variants || variants.length === 0) return "";
    return variants
      .map((v) => `${v.label}:${v.price}:${v.originalPrice || ""}`)
      .join(";");
  };

  const handleExportCSV = () => {
    const headers = [
      "Name",
      "Category",
      "Price",
      "OriginalPrice",
      "Stock",
      "Image",
      "Description",
      "IsNewArrival",
      "Featured",
      "FeaturedOrder",
      "Variants"
    ];

    const rows = products.map((p) => [
      csvEscape(p.name),
      csvEscape(p.category),
      csvEscape(p.price ?? ""),
      csvEscape(p.originalPrice ?? ""),
      csvEscape(p.stock ?? ""),
      csvEscape(p.image ?? ""),
      csvEscape(p.description ?? ""),
      csvEscape(p.isNewArrival ? "TRUE" : "FALSE"),
      csvEscape(p.featured ? "TRUE" : "FALSE"),
      csvEscape(p.featuredOrder ?? ""),
      csvEscape(encodeVariants(p.variants))
    ]);

    const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const dateStr = new Date().toISOString().slice(0, 10);
    link.setAttribute("download", `sunrise-stationery-products-${dateStr}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const categories = ["All", ...new Set(products.map((p) => p.category).filter(Boolean))];

  const filteredProducts = products.filter((product) => {
    const matchesCategory = selectedCategory === "All" || product.category === selectedCategory;
    const searchValue = productSearch.trim().toLowerCase();
    const matchesSearch = !searchValue || [
      product.name,
      product.category,
      product.description
    ].some((value) => value?.toLowerCase().includes(searchValue));

    return matchesCategory && matchesSearch;
  });

  return (
    <div className="admin-products">
      <div className="admin-header-row">
        <h2 className="admin-section-title">Products</h2>
        <div className="admin-header-actions">
          <button
            className="btn-export"
            onClick={handleExportCSV}
            disabled={products.length === 0}
          >
            ⬇ Export to CSV
          </button>
          <button className="btn-add" onClick={handleAddClick}>
            {showForm ? "Cancel" : "+ Add Product"}
          </button>
        </div>
      </div>

      {showForm && (
        <form className="product-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Product name"
              />
            </div>
            <div className="form-group">
              <label>Category *</label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
                placeholder="e.g. Pens, Notebooks"
              />
            </div>
            <div className="form-group">
              <label>Price (₹) *</label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                required
                min="0"
                placeholder="0"
              />
            </div>
            <div className="form-group">
              <label>Original Price / MRP (₹)</label>
              <input
                type="number"
                value={formData.originalPrice}
                onChange={(e) => setFormData({ ...formData, originalPrice: e.target.value })}
                min="0"
                placeholder="Leave blank for no discount"
              />
            </div>
            <div className="form-group">
              <label>Stock *</label>
              <input
                type="number"
                value={formData.stock}
                onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                required
                min="0"
                placeholder="0"
              />
            </div>
           <div className="form-group form-full">
  <label>Product Image</label>
  <div className="image-upload-wrapper">
    <label className="file-upload-btn">
      Choose Image
      <input
        type="file"
        accept="image/*"
        onChange={handleImageFileChange}
        disabled={imageUploading}
        style={{ display: "none" }}
      />
    </label>
    {imageUploading && <span className="upload-status">Uploading...</span>}
    {formData.image && !imageUploading && (
      <span className="upload-status success">✓ Image uploaded</span>
    )}
  </div>
  {formData.image && (
    <img
      src={formData.image}
      alt="preview"
      className="image-preview"
    />
  )}
</div>
            <div className="form-group form-full">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
                placeholder="Product description..."
              />
            </div>
            <div className="form-group form-full checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isNewArrival}
                  onChange={(e) => setFormData({ ...formData, isNewArrival: e.target.checked })}
                />
                <span>Mark as New Arrival</span>
              </label>
            </div>

            {/* NEW: Featured toggle */}
            <div className="form-group form-full checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                />
                <span>⭐ Feature this product (show at top of storefront)</span>
              </label>
            </div>

            {/* NEW: Featured order — only show when featured is checked */}
            {formData.featured && (
              <div className="form-group">
                <label>Featured Order (lower number = shown first)</label>
                <input
                  type="number"
                  value={formData.featuredOrder}
                  onChange={(e) => setFormData({ ...formData, featuredOrder: e.target.value })}
                  min="0"
                  placeholder="0"
                />
              </div>
            )}
          </div>

          <div className="variant-section">
            <label>Variants</label>
            <div className="variant-input-row">
              <input
                type="text"
                placeholder="Variant label (e.g. Blue)"
                value={variantInput.label}
                onChange={(e) => setVariantInput({ ...variantInput, label: e.target.value })}
              />
              <input
                type="number"
                placeholder="Price"
                value={variantInput.price}
                onChange={(e) => setVariantInput({ ...variantInput, price: e.target.value })}
                min="0"
              />
              <input
                type="number"
                placeholder="MRP (optional)"
                value={variantInput.originalPrice}
                onChange={(e) => setVariantInput({ ...variantInput, originalPrice: e.target.value })}
                min="0"
              />
              <button type="button" className="btn-variant-add" onClick={addVariant}>+ Add</button>
            </div>
            {formData.variants.length > 0 && (
              <div className="variant-list">
                {formData.variants.map((v, idx) => (
                  <div key={idx} className="variant-chip">
                    <span>
                      {v.label} -{" "}
                      {v.originalPrice && v.originalPrice > v.price && (
                        <s style={{ opacity: 0.6, marginRight: 4 }}>{fmt(v.originalPrice)}</s>
                      )}
                      {fmt(v.price)}
                    </span>
                    <button type="button" onClick={() => removeVariant(idx)}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-save" disabled={imageUploading}>
              {editingId ? "Update Product" : "Add Product"}
            </button>
          </div>
        </form>
      )}

      {!loading && products.length > 0 && (
        <>
          <div className="product-filter-bar">
            <label className="admin-product-search">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="search"
                value={productSearch}
                onChange={(event) => setProductSearch(event.target.value)}
                placeholder="Search products..."
                aria-label="Search products"
              />
            </label>

            <label className="admin-category-select">
              <span>Category</span>
              <select
                value={selectedCategory}
                onChange={(event) => setSelectedCategory(event.target.value)}
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category === "All"
                      ? `All categories (${products.length})`
                      : `${category} (${products.filter((product) => product.category === category).length})`}
                  </option>
                ))}
              </select>
            </label>

            <button
              type="button"
              className={`btn-manage-categories ${showCategoryManager ? "active" : ""}`}
              onClick={() => setShowCategoryManager((current) => !current)}
            >
              Manage Categories
            </button>
          </div>

          {showCategoryManager && (
            <div className="category-manager" aria-label="Category visibility settings">
              <div className="category-manager-header">
                <div>
                  <h3>Storefront Categories</h3>
                  <p>Hide categories from shoppers without deleting their products.</p>
                </div>
              </div>

              <div className="category-manager-list">
                {categories.slice(1).map((category) => {
                  const isHidden = hiddenCategories.includes(category);
                  const productCount = products.filter((product) => product.category === category).length;

                  return (
                    <div key={category} className="category-manager-item">
                      <div>
                        <strong>{category}</strong>
                        <span>{productCount} products</span>
                      </div>
                      <button
                        type="button"
                        className={`category-visibility-status ${isHidden ? "hidden" : "visible"}`}
                        onClick={() => toggleCategoryVisibility(category)}
                      >
                        {isHidden ? "Hidden" : "Visible"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {loading ? (
        <div className="admin-loading">
          <div className="spinner"></div>
        </div>
      ) : products.length === 0 ? (
        <div className="admin-empty">No products yet.</div>
      ) : filteredProducts.length === 0 ? (
        <div className="admin-empty">No products match these filters.</div>
      ) : (
        <div className="products-table-wrapper">
          <table className="products-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Category</th>
                <th>Price</th>
                <th>MRP</th>
                <th>Stock</th>
                <th>Variants</th>
                <th>New?</th>
                <th>Featured</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (
                <tr key={product.id}>
                  <td>
                    <img
                      src={product.image || "https://picsum.photos/60/60"}
                      alt={product.name}
                      className="product-thumb"
                      onError={(e) => { e.target.src = "https://picsum.photos/60/60"; }}
                    />
                  </td>
                  <td className="product-cell-name">{product.name}</td>
                  <td>
                    {product.category}
                    {hiddenCategories.includes(product.category) && (
                      <span className="hidden-tag hidden-tag-inline">Hidden</span>
                    )}
                  </td>
                  <td>{fmt(product.price || 0)}</td>
                  <td>
                    {product.originalPrice && product.originalPrice > (product.price || 0)
                      ? fmt(product.originalPrice)
                      : "—"}
                  </td>
                  <td>
                    <span className={`stock-pill ${product.stock <= 5 ? "low" : ""} ${product.stock === 0 ? "out" : ""}`}>
                      {product.stock}
                    </span>
                  </td>
                  <td>{product.variants?.length || 0}</td>
                  <td>
                    {product.isNewArrival && <span className="new-arrival-pill">NEW</span>}
                  </td>
                  <td>
                    {product.featured && (
                      <span className="new-arrival-pill" title={`Order: ${product.featuredOrder ?? 0}`}>
                        ★ {product.featuredOrder ?? 0}
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="product-actions">
                      <button className="btn-edit" onClick={() => handleEdit(product)}>Edit</button>
                      <button className="btn-delete" onClick={() => handleDelete(product.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;